import React, {useEffect, useState} from "react";
import {useSearchParams, useNavigate} from "react-router-dom";
import graphImg from "../../../../assets/images/noun-line-graph-251713-9B9B9B.svg";
import noServersImg from "../../../../assets/images/noun-serverless-4339393-9B9B9B.svg";
import {Alert, Card, CardTitle, CardText, Col, Row, Table, Spinner} from "reactstrap";
import {VsiBubble, VsiH5, VsiHr} from "../../../../styled/App.styled";
import AtAGlance from "../../../../components/AtAGlance";
import {useVsiAppContext} from "../../../../stores/VsiApp";
import Grid from "../../../../components/Grid";
import PieChart from "../../../../components/PieChart";
import {useDataWithRoute, useTitleFromComponent} from "../../../../utils/hooks";
import NoteEditor from "../../../../components/NoteEditor";
import {highPerformingHostsDefs} from "../../../../utils/gridColumnDefs/storage/device/deviceSummary";
import themeStyles from "../../../../utils/themeStyles";
import { healthColorAlertFormatter } from "../../../../utils/gridFormatters";
import Stockchart from "../../../../components/Stockchart";

const deviceSummaryAag = [
    {
        field: "model",
        mobile: true,
    },
    {
        field: "vendor",
        mobile: true
    },
    {
        field: "firmware",
        mobile: true,
    },

    {
        title: "Array Status",
        field: "status",
        mobile: true,
    },
    {
        field: "data_center",
        mobile: true,
    },
    {
        field: "classification",
        mobile: true,
    },
    {
        field: "geography"
    },
    {
        field: "tier"
    },
    {
        title: "Orphans",
        field: "orphan"
    }
];

const summaryConfig = [
    {
        fieldValue: 'configuration_and_setup',
        title: 'Configuration & Setup',
    },
    {
        fieldValue: 'balance',
        title: 'Balance',
    },
    {
        fieldValue: 'server_volume_mapping',
        title: 'Server Volume Mapping',
    },
    {
        fieldValue: 'errors_and_other_alerts',
        title: 'Errors & Other Alerts',
    },
    {
        fieldValue: 'storage_layout',
        title: 'Storage Layout',
    },
    {
        fieldValue: 'volumes_and_snapshots',
        title: 'Volumes & Snapshots',
    }
]

const topHostsConfig = [
    {
        hostName: 'host_1_name',
        allocated: 'host_1_allocated_size',
        iops: 'host_1_iops',
        latency: 'host_1_latency'
    },
    {
        hostName: 'host_2_name',
        allocated: 'host_2_allocated_size',
        iops: 'host_2_iops',
        latency: 'host_2_latency'
    },
    {
        hostName: 'host_3_name',
        allocated: 'host_3_allocated_size',
        iops: 'host_3_iops',
        latency: 'host_3_latency'
    },
    {
        hostName: 'host_4_name',
        allocated: 'host_4_allocated_size',
        iops: 'host_4_iops',
        latency: 'host_4_latency'
    },
    {
        hostName: 'host_5_name',
        allocated: 'host_5_allocated_size',
        iops: 'host_5_iops',
        latency: 'host_5_latency'
    }
]

export default function DeviceSummary({context, title}) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [deviceSummary, setDeviceSummary] = useState({});
    const [missingDataWarningVisible, setMissingDataWarningVisible] = useState(false);
    const { theme, client, periodId } = useVsiAppContext();

    const [isLoading, setIsLoading] = useState(true);
    const deviceName = decodeURIComponent(searchParams.get("device_name"));
    const [capacityTrendSeries, setCapacityTrendSeries] = useState([]);
    const [pieChartSeries, setPieChartSeries] = useState([]);
    const [performanceTrendSeries, setPerformanceTrendSeries] = useState([]);
    const [topHostsData, setTopHostsData] = useState([]);

    useTitleFromComponent(`${deviceName} / ${title}`)

    const [data] = useDataWithRoute(`/api/v6/report/device/device-summary?client_id=${client.clientId}&device_name=${encodeURIComponent(deviceName)}&period_id=${periodId}`);

    const checkForMissingData = data => data.vendor === "Missing" || data.classification === "Missing" || data.geography === "Missing";

    const suppressMissingDataWarningPermanently = () => {
        window.localStorage.setItem('suppressMissingDataWarning', "true");
        setMissingDataWarningVisible(false);
    }
    const getDeviceSummary = data => {
        if (data && data[0]) {
            const suppressMissingDataWarning = window.localStorage.getItem('suppressMissingDataWarning') === 'true';
            if (!suppressMissingDataWarning && checkForMissingData(data[0])) {
                setMissingDataWarningVisible(true)
            }
            let topHosts = topHostsConfig.map(({hostName, allocated, iops, latency}) => {return {
                host_name: data?.[0]?.[hostName],
                allocated: data?.[0]?.[allocated],
                iops: data?.[0]?.[iops],
                latency: data?.[0]?.[latency]
            }})
            topHosts = topHosts.filter(host => host.host_name && host.allocated && host.iops && host.latency)
            setTopHostsData(topHosts)
            setDeviceSummary(data?.[0] || {});
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getDeviceSummary(data?.recordset);
    }, [data]);

    const dismissMissingDataWarning = () => setMissingDataWarningVisible(false);

    const getCapacityTrendChart = () => {
        // first find out if we even have enough data to graph.  we need two points at a minimum.
        if (deviceSummary && deviceSummary.capacity_trend_date_1 && deviceSummary.capacity_trend_date_2) {
            const utilizationLineData = [];
            const capacityLineData = [];
            const allocatedLineData = [];
            const dataReductionRatioLineData = [];

            // we know we have at least two data points.
            const graphDate1 = Date.parse(deviceSummary.capacity_trend_date_1?.split?.('T')?.[0]);
            const graphDate2 = Date.parse(deviceSummary.capacity_trend_date_2?.split?.('T')?.[0]);

            utilizationLineData.push([graphDate1, deviceSummary.utilization_1] || 0);
            utilizationLineData.push([graphDate2, deviceSummary.utilization_2] || 0);

            capacityLineData.push([graphDate1, deviceSummary.capacity_1] || 0);
            capacityLineData.push([graphDate2, deviceSummary.capacity_2] || 0);

            allocatedLineData.push([graphDate1, deviceSummary.allocation_1] || 0);
            allocatedLineData.push([graphDate2, deviceSummary.allocation_2] || 0);

            dataReductionRatioLineData.push([graphDate1, deviceSummary.data_reduction_1] || 0);
            dataReductionRatioLineData.push([graphDate2, deviceSummary.data_reduction_2] || 0);

            // let's try a third!
            if (deviceSummary.capacity_trend_date_3) {
                const graphDate3 = Date.parse(deviceSummary.capacity_trend_date_3?.split?.('T')?.[0]);
                utilizationLineData.push([graphDate3, deviceSummary.utilization_3] || 0);
                capacityLineData.push([graphDate3, deviceSummary.capacity_3] || 0);
                allocatedLineData.push([graphDate3, deviceSummary.allocation_3] || 0);
                dataReductionRatioLineData.push([graphDate3, deviceSummary.data_reduction_3] || 0);
            }

            if (deviceSummary.capacity_trend_date_4) {
                const graphDate4 = Date.parse(deviceSummary.capacity_trend_date_4?.split?.('T')?.[0]);
                utilizationLineData.push([graphDate4, deviceSummary.utilization_4] || 0);
                capacityLineData.push([graphDate4, deviceSummary.capacity_4] || 0);
                allocatedLineData.push([graphDate4, deviceSummary.allocation_4] || 0);
                dataReductionRatioLineData.push([graphDate4, deviceSummary.data_reduction_4] || 0);
            }

            if (deviceSummary.capacity_trend_date_5) {
                const graphDate5 = Date.parse(deviceSummary.capacity_trend_date_5?.split?.('T')?.[0]);
                utilizationLineData.push([graphDate5, deviceSummary.utilization_5] || 0);
                capacityLineData.push([graphDate5, deviceSummary.capacity_5] || 0);
                allocatedLineData.push([graphDate5, deviceSummary.allocation_5] || 0);
                dataReductionRatioLineData.push([graphDate5, deviceSummary.data_reduction_5] || 0);
            }

            const roundedDrrData = dataReductionRatioLineData.map((drr) => {
                return [drr[0], Number(parseFloat(drr[1]).toFixed(2))];
            });

            // sort everything so highcharts doesn't bitch about it
            utilizationLineData.sort((a, b) => a[0] - b[0]);
            capacityLineData.sort((a, b) => a[0] - b[0]);
            allocatedLineData.sort((a, b) => a[0] - b[0]);
            dataReductionRatioLineData.sort((a, b) => a[0] - b[0]);


            // this is gross but it's how highcharts likes it!  might be neater if we broke this up.
            const capacityTrendChartConfig = {
                "yAxis": [
                    {"title": {"text": "Used (GiB)"}},
                    {
                        "title": {
                            "text": "Data Reduction Ratio"
                        },
                        "opposite": true,
                        "labels": {
                            "format": "{value:.2f}",
                        }
                    }],
                "colors": ["#099c9d", themeStyles[theme].textColor, "#B0EA3B", "#ff8d02", "#004577"]
            }

            const capacityTrendDataTemplate = [
                {
                    "name": "Utilization",
                    "type": null,
                    "data": utilizationLineData,
                    "dashStyle": "Solid",
                    "yAxis": 0,
                    "tooltip": {
                        "valueDecimals": 2
                    },
                    "marker": {
                        "enabled": true
                    },
                    "zoneAxis": null,
                    "zones": null
                },
                {
                    "name": "Capacity",
                    "type": null,
                    "data": capacityLineData,
                    "dashStyle": "Solid",
                    "yAxis": 0,
                    "tooltip": {
                        "valueDecimals": 2
                    },
                    "marker": {
                        "enabled": true
                    },
                    "zoneAxis": null,
                    "zones": null
                },
                {
                    "name": "Allocation",
                    "type": null,
                    "data": allocatedLineData,
                    "dashStyle": "Solid",
                    "yAxis": 0,
                    "tooltip": {
                        "valueDecimals": 2
                    },
                    "marker": {
                        "enabled": true
                    },
                    "zoneAxis": null,
                    "zones": null
                },
                {
                    "name": "Data Reduction Ratio",
                    "type": null,
                    "data": roundedDrrData,
                    "dashStyle": "Solid",
                    "yAxis": 1,
                    "tooltip": {
                        "valueDecimals": 2
                    },
                    "marker": {
                        "enabled": true
                    },
                    "zoneAxis": null,
                    "zones": null
                }
            ];

            if(!capacityTrendSeries.length) {
                setCapacityTrendSeries(capacityTrendDataTemplate)
            }

            return (<Stockchart navDisabled={true}
                                height={450}
                                series={capacityTrendDataTemplate}
                                rangeSelectorDisabled={true}
                                config={capacityTrendChartConfig}/>);
        } else {
            // return "not enough data card.
            return (<Card style={{maxHeight: 480}} className={`report-card p-2 nav-card-${theme}`}>
                <img src={graphImg} style={{maxHeight: 360}}/>
                <CardTitle tag="h5" style={{color: themeStyles[theme].textColor, paddingLeft: 6, paddingRight: 6}}>
                    Not enough data
                </CardTitle>
                <CardText style={{color: themeStyles[theme].textColor, paddingLeft: 6, paddingRight: 6}}>
                    There aren't enough data points to graph yet. As you collect more data for this device, you'll see a
                    trend chart for capacity metrics here.
                </CardText>
            </Card>);
        }
    };

    useEffect(() => {
        if(Object.keys(deviceSummary).length) {
            setPieChartSeries([{
                name: "Drives by Type",
                data: [
                    {name: "SSD", y: deviceSummary.ssd_count},
                    {name: "SAS", y: deviceSummary.sas_count},
                    {name: "SATA", y: deviceSummary.sata_count},
                    {name: "Fibre", y: deviceSummary.fibre_count},
                    {name: "Unknown", y: deviceSummary.unknown_count}
                ]
            }])
        }
    }, [deviceSummary])

    const getDriveByTypePieChart = () => {
        const pieData = [
            {name: "SSD", y: deviceSummary.ssd_count},
            {name: "SAS", y: deviceSummary.sas_count},
            {name: "SATA", y: deviceSummary.sata_count},
            {name: "Fibre", y: deviceSummary.fibre_count},
            {name: "Unknown", y: deviceSummary.unknown_count}
        ];
        return ( <PieChart
            height={450}
            includeLegend={true}
            series={[{
                name: "Drives by Type",
                data: pieData
            }]}
        />);
    }

    const getPerformanceTrendChart = () => {
        // debugger;
        // like with capacity, you need at least two points to graph so make sure we have at least two dates
        if (deviceSummary && deviceSummary.performance_trend_date_1 && deviceSummary.performance_trend_date_2) {
            const iopsData = [];
            const latencyData = [];

            const graphDate1 = Date.parse(deviceSummary.performance_trend_date_1?.split?.('T')?.[0]);
            const graphDate2 = Date.parse(deviceSummary.performance_trend_date_2?.split?.('T')?.[0]);

            iopsData.push([graphDate1, deviceSummary.iops_1] || 0);
            iopsData.push([graphDate2, deviceSummary.iops_2] || 0);

            latencyData.push([graphDate1, deviceSummary.latency_1] || 0);
            latencyData.push([graphDate2, deviceSummary.latency_2] || 0);

            if (deviceSummary && deviceSummary.performance_trend_date_3) {
                const graphDate3 = Date.parse(deviceSummary.performance_trend_date_3?.split?.('T')?.[0]);
                iopsData.push([graphDate3, deviceSummary.iops_3 || 0]);
                latencyData.push([graphDate3, deviceSummary.latency_3 || 0]);
            }

            if (deviceSummary && deviceSummary.performance_trend_date_4) {
                const graphDate4 = Date.parse(deviceSummary.performance_trend_date_4?.split?.('T')?.[0]);
                iopsData.push([graphDate4, deviceSummary.iops_4 || 0]);
                latencyData.push([graphDate4, deviceSummary.latency_4 || 0]);
            }

            if (deviceSummary && deviceSummary.performance_trend_date_5) {
                const graphDate5 = Date.parse(deviceSummary.performance_trend_date_5?.split?.('T')?.[0]);
                iopsData.push([graphDate5, deviceSummary.iops_5 || 0]);
                latencyData.push([graphDate5, deviceSummary.latency_5 || 0]);
            }

            // if you don't make sure the dates are sorted, highcharts has a big nee nee fit.
            iopsData.sort((a, b) => a[0] - b[0]);
            latencyData.sort((a, b) => a[0] - b[0]);

            // nee nee fit averted!  generate the stockchart...
            const capacityTrendChartConfig = {
                "yAxis": [
                    {"title": {"text": "IOPS"}},
                    {
                        "title": {
                            "text": "Latency"
                        },
                        "opposite": true
                    }],
                "colors": ["#099c9d", "#B0EA3B"]
            }

            const performanceTrendDataTemplate = [{
                "name": "IOPS",
                "type": null,
                "data": iopsData,
                "dashStyle": "Solid",
                "yAxis": 0,
                "tooltip": {
                    "valueDecimals": 2
                },
                "marker": {
                    "enabled": true
                },
                "zoneAxis": null,
                "zones": null
            },
                {
                    "name": "Latency",
                    "type": null,
                    "data": latencyData,
                    "dashStyle": "Solid",
                    "yAxis": 1,
                    "tooltip": {
                        "valueDecimals": 2
                    },
                    "marker": {
                        "enabled": true
                    },
                    "zoneAxis": null,
                    "zones": null
                }
            ];

            if(!performanceTrendSeries.length) {
                setPerformanceTrendSeries(performanceTrendDataTemplate)
            }

            return (<Stockchart navDisabled={true}
                                height={450}
                                series={performanceTrendDataTemplate}
                                rangeSelectorDisabled={true}
                                config={capacityTrendChartConfig}/>);


        } else {
            return (<div style={{marginTop: 16}}><Card className={`report-card p-2 nav-card-${theme}`}>
                <img src={graphImg} style={{maxHeight: 360}}/>
                <CardTitle tag="h5" style={{color: themeStyles[theme].textColor, paddingLeft: 6, paddingRight: 6}}>
                    Not enough data
                </CardTitle>
                <CardText style={{color: themeStyles[theme].textColor, paddingLeft: 6, paddingRight: 6}}>
                    There aren't enough data points to graph yet. As you collect more data for this device, you'll see a
                    trend chart for capacity metrics here.
                </CardText>
            </Card></div>);
        }


    };

    const onHostClick = (e) => {
        navigate(`/reports/hosts/host/host-trends?host_name=${encodeURI(e.data.host_name)}&device_name=${encodeURI(deviceName)}`)
    }

    const getTop5HostData = () => {
        // there should be at least one host.
        if (deviceSummary && deviceSummary.host_1_name) {
            return (<Grid style={{height: 270}} rowGroupPanelShow={false}
                            gridText={'Double click to see detailed host reports'}
                            rowDoubleClicked={onHostClick}
                            hideRecordCount={true}
                            hideColumnControls={true}
                            data={topHostsData}
                            columnDefs={highPerformingHostsDefs}/>);
        } else {
            return (<Card style={{height: 270, border: '1px solid #666666'}}
                          className={`report-card p-2 nav-card-${theme}`}>
                <img src={noServersImg} style={{maxHeight: 180, marginBottom: 26, marginTop: 6}}/>
                <CardTitle tag="h5" style={{
                    color: themeStyles[theme].textColor,
                    paddingLeft: 6,
                    paddingRight: 6,
                    textAlign: 'center'
                }}>
                    There aren't any hosts to show for this device.
                </CardTitle>

            </Card>);
        }
    };

    const getHealthSummaryTable = () => {
        return (
            <div onDoubleClick={onHealthSummaryDoubleClick}>
                <Table>
                    <thead>
                    <tr>
                        {summaryConfig.map(({fieldValue, title}, index) => {
                            return <th key={`health-title-${index}-${fieldValue}`} style={{textAlign: 'center', color: themeStyles[theme].textColor}}>{title}</th>
                        })}
                    </tr>

                    </thead>
                    <tbody>
                    <tr>
                        {summaryConfig.map(({fieldValue}, index) => {
                            return <td align={"center"} key={`health-field-${index}-${fieldValue}`}>{healthColorAlertFormatter({value: deviceSummary[fieldValue]})}</td>
                        })}
                    </tr>
                    </tbody>
                </Table>
            </div>
        );
    }
    const onCapacityTrendDoubleClick = (e) => {
        // example: http://10.2.251.4:3000/reports/storage/device/device-trends?device_name=APDSCVMWVCTR02.mclaneco.com&period_id=3282&metric=all_history&axis_1=used&axis_2=drr
        const url = `/reports/storage/device/device-trends?device_name=${deviceName}&period_id=${periodId}&metric=all_history&axis_1=used&axis_2=drr`;
        navigate(url);
    };

    const onPerformanceTrendDoubleCLick = e => {
        // example: http://10.2.251.4:3000/reports/storage/device/device-trends?device_name=APDSCVMWVCTR02.mclaneco.com&period_id=3282&metric=all_history&axis_1=iops&axis_2=latency
        const url = `/reports/storage/device/device-trends?device_name=${deviceName}&period_id=${periodId}&metric=all_history&axis_1=iops&axis_2=latency`;
        navigate(url);
    }

    const onHealthSummaryDoubleClick = e => {
        // example: http://10.2.251.4:3000/reports/storage/device/device-health?device_name=APDSCVMWVCTR02.mclaneco.com&wwn=5c6e6383-8747-446e-bcb6-6a2d1ab4747b&period_id=3282
        const url = `/reports/storage/device/device-health?device_name=${encodeURI(deviceName)}&period_id=${periodId}`;
        navigate(url);
    }

    const onDriveByTypeDoubleClick = e => {
        const url = `/reports/storage/device/physical-diagram?device_name=${encodeURI(deviceName)}&wwn=${deviceSummary.wwn}&period_id=${periodId}`;
        navigate(url);
    }

    return (
        <Card className={`report-card p-2 nav-card-${theme}`}>
            <VsiH5>Configuration Summary</VsiH5>
            <VsiBubble className={`mb-2 p-2`} autobotInfo={{
                data: deviceSummary,
                config: deviceSummaryAag
            }}>
                <AtAGlance config={deviceSummaryAag} data={{
                    model: deviceSummary.model,
                    vendor: deviceSummary.vendor,
                    firmware: deviceSummary.firmware,
                    status: deviceSummary.status,
                    data_center: deviceSummary.data_center,
                    classification: deviceSummary.classification,
                    geography: deviceSummary.geography,
                    tier: deviceSummary.tier,
                    orphan: deviceSummary.orphan
                }} size={"lg"}/>
            </VsiBubble>
            <Alert color="warning" toggle={dismissMissingDataWarning} isOpen={missingDataWarningVisible}>
                <i className="fa-solid fa-triangle-exclamation"/>&nbsp;&nbsp;This device is missing Business Unit SAN
                Capacity Data. Without it, some of your fields will show "Missing" or N/A.  &nbsp;&nbsp;
                <a href={"#"} onClick={suppressMissingDataWarningPermanently}>Never show this again</a>
            </Alert>

            <VsiHr/>
            <VsiBubble autobotName='DeviceSummaryGraphs' autobotInfo={{ data: {
                capacityStockChart: {data: capacityTrendSeries},
                pieChart: {data: pieChartSeries},
                performanceStockChart: {data: performanceTrendSeries}
            } }}>
                <Row className={"mb-2"} style={{paddingLeft: 6, paddingRight: 6}}>

                    <Col xs={12} lg={4}>
                        <div onDoubleClick={onCapacityTrendDoubleClick} style={{cursor: 'pointer'}}>
                            <VsiH5>Capacity Trend</VsiH5>
                            {isLoading ? <Spinner color={"primary"}/> : getCapacityTrendChart()}
                        </div>
                    </Col>


                    <Col xs={12} lg={4}>
                        <div onDoubleClick={onDriveByTypeDoubleClick}>
                            <VsiH5>Drives by Type</VsiH5>
                            {getDriveByTypePieChart()}
                        </div>
                    </Col>


                    <Col xs={12} lg={4}>
                        <div onDoubleClick={onPerformanceTrendDoubleCLick}>
                            <VsiH5>Device Performance</VsiH5>
                            {isLoading ? <Spinner color={"primary"}/> : getPerformanceTrendChart()}
                        </div>
                    </Col>
                </Row>
            </VsiBubble>
            <VsiBubble autobotName='SummaryTable' autobotInfo={{
                data: deviceSummary,
                config: summaryConfig
            }}>
                {getHealthSummaryTable()}
            </VsiBubble>

            <VsiBubble noAutobot>
                <Row style={{paddingLeft: 6, paddingRight: 6}}>
                    <Col xs={12} lg={6}>
                        <VsiH5>Top High Performing Hosts</VsiH5>
                        {getTop5HostData()}
                    </Col>
                    <Col xs={12} lg={6}>
                        <VsiH5 style={{marginTop: 1}}>Device Notes</VsiH5>
                        <NoteEditor clientId={client.clientId} noteKey={deviceName}/>
                    </Col>

                </Row>
            </VsiBubble>


        </Card>
    )
}
